import { Box, Chip, alpha } from "@mui/material";
import { useState } from "react";

interface Props {
  options: string[];
  /** Disable / hide chips when not on the latest assistant turn —
   *  scrolling back through history shouldn't let the user click an
   *  old chip and dispatch it as a new turn. */
  interactive: boolean;
  isStreaming: boolean;
  onPick: (option: string) => void;
}

/**
 * One-click reply chips for simple multiple-choice answers. The
 * agent calls `suggest_quick_replies(options=[...])` from
 * `backend/requirements/agent_tools.py`; this component renders
 * the result on the latest assistant message.
 *
 * Click → dispatches the option text as the user's next turn via
 * `onPick` (which routes through the standard chat send pipeline,
 * so the click reads identically to typing the same answer).
 *
 * Old-message chips are rendered for transparency (the user can
 * see what they were offered) but are dimmed and non-interactive.
 */
export function QuickReplyChips({
  options,
  interactive,
  isStreaming,
  onPick,
}: Props) {
  const [picked, setPicked] = useState<string | null>(null);
  if (options.length === 0) return null;

  const usable = interactive && !isStreaming && picked === null;

  const handle = (opt: string) => {
    setPicked(opt);
    onPick(opt);
  };

  return (
    <Box
      sx={{
        mt: 1.25,
        display: "flex",
        flexWrap: "wrap",
        gap: 0.75,
      }}
    >
      {options.map((opt) => {
        const isPicked = picked === opt;
        return (
          <Chip
            key={opt}
            label={opt}
            size="small"
            clickable={usable}
            onClick={usable ? () => handle(opt) : undefined}
            sx={(t) => ({
              fontSize: "0.78rem",
              height: 28,
              borderRadius: 1,
              fontFamily: t.fonts?.sans,
              border: `1px solid ${alpha(
                t.palette.editorial?.main ?? t.palette.warning.main,
                isPicked ? 0.6 : 0.32,
              )}`,
              background: isPicked
                ? alpha(
                    t.palette.editorial?.main ?? t.palette.warning.main,
                    0.18,
                  )
                : usable
                  ? alpha(
                      t.palette.editorial?.main ?? t.palette.warning.main,
                      0.06,
                    )
                  : "transparent",
              color: usable
                ? t.palette.text.primary
                : t.palette.text.disabled,
              transition: `background ${t.motion?.fast ?? "120ms"}`,
              cursor: usable ? "pointer" : "default",
              "&:hover": usable
                ? {
                    background: alpha(
                      t.palette.editorial?.main ?? t.palette.warning.main,
                      0.14,
                    ),
                  }
                : undefined,
            })}
          />
        );
      })}
    </Box>
  );
}
